import {
  Event,
  EventInput,
  LocationType,
  Shift,
  ShiftInput,
  Visibility,
  WaiverRequirement,
} from '@amzn/red-velvet-api';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { SelectProps } from '@amzn/awsui-components-react';

export const EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

export function formatCharacterCount(currentLength: number, maxLength: number): string {
  return `${currentLength}/${maxLength}`;
}

export function generateEventStartDateWithMonthAndDayToDisplay(
  selectedTimezone: string | null,
  language: string,
  event: Event
) {
  return event.startTimestamp?.toLocaleDateString(language, {
    ...EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS,
    timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function generateEventEndDateWithMonthAndDayToDisplay(
  selectedTimezone: string | null,
  language: string,
  event: Event
) {
  return event.endTimestamp?.toLocaleDateString(language, {
    ...EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS,
    timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function isSameDayFullDay(start: Date, end: Date): boolean {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const isSameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();
  const isFullDay =
    startDate.getHours() === 0 &&
    startDate.getMinutes() === 0 &&
    startDate.getSeconds() === 0 &&
    endDate.getHours() === 23 &&
    endDate.getMinutes() === 59 &&
    endDate.getSeconds() === 0;

  // Also check UTC for backward compatibility with existing events stored in UTC
  const isSameDayUTC =
    startDate.getUTCFullYear() === endDate.getUTCFullYear() &&
    startDate.getUTCMonth() === endDate.getUTCMonth() &&
    startDate.getUTCDate() === endDate.getUTCDate();
  const isFullDayUTC =
    startDate.getUTCHours() === 0 &&
    startDate.getUTCMinutes() === 0 &&
    startDate.getUTCSeconds() === 0 &&
    endDate.getUTCHours() === 23 &&
    endDate.getUTCMinutes() === 59 &&
    endDate.getUTCSeconds() === 0;

  return (isSameDay && isFullDay) || (isSameDayUTC && isFullDayUTC);
}

export function isUnlistedEvent(event: Event): boolean {
  return (
    event.source === 'VC' &&
    !!event.startTimestamp &&
    !!event.endTimestamp &&
    isSameDayFullDay(event.startTimestamp, event.endTimestamp) &&
    event.status !== 'DRAFT' // avoid confusion with listed events in draft
  );
}

export function findOption(options: SelectProps.Options, value: string): OptionDefinition | null {
  for (const opt of options) {
    if ('value' in opt && opt.value === value) return opt;
    if ('options' in opt) {
      const found = opt.options?.find((o) => o.value === value);
      if (found) return found;
    }
  }
  return null;
}

export function mapEventToEventInput(event: Event): EventInput {
  // Derive locationType from the event's location object.
  // The backend now returns locationType on location (ON_SITE, OFF_SITE, or VIRTUAL).
  // For backward compat with older events, fall back to deriving from amazonBuilding.
  const locType = event.location?.locationType;
  const resolvedLocationType: LocationType =
    locType === LocationType.VIRTUAL ||
    locType === LocationType.ON_SITE ||
    locType === LocationType.OFF_SITE
      ? locType
      : !event.location || (!event.location.name && !event.location.city && !event.location.street)
        ? LocationType.VIRTUAL
        : event.location.amazonBuilding
          ? LocationType.ON_SITE
          : LocationType.OFF_SITE;

  // The location object in DynamoDB may be stale from a previous locationType.
  // Discard only when there's a clear mismatch: ON_SITE data in OFF_SITE type or vice versa.
  const hasOnCampusData = !!event.location?.amazonBuilding;
  const locationMatchesType =
    resolvedLocationType === LocationType.ON_SITE ? true : !hasOnCampusData;

  const location =
    resolvedLocationType === LocationType.VIRTUAL
      ? { locationType: LocationType.VIRTUAL }
      : !locationMatchesType
        ? undefined
        : {
            ...event.location,
            locationType: resolvedLocationType,
            address_string: [
              event.location?.street,
              event.location?.city,
              event.location?.province,
              event.location?.postal_code,
              event.location?.country,
            ]
              .filter(Boolean)
              .join(', '),
          };

  return {
    title: event.title,
    description: event.description,
    shortDescription: event.shortDescription,
    location,
    locationVirtualNotes:
      event.locationVirtualNotes ??
      (resolvedLocationType === LocationType.VIRTUAL ? event.location?.notes : undefined),
    timeZone: event.timeZone,
    focusArea: event.focusArea,
    primaryActivity: event.primaryActivity,
    usedAciGrant: event.usedAciGrant,
    grantId: event.grantId,
    additionalCoordinators: event.additionalCoordinators,
    externalPlanningPartner: event.externalPlanningPartner,
    otherPartner: event.otherPartner,
    elgAffiliations: event.elgAffiliations,
    eventType: event.eventType,
    allowGuests: event.allowGuests,
    waiverUrl: event.waiver?.url,
    waiverRequired: (event.waiver?.acceptance as WaiverRequirement) ?? WaiverRequirement.NONE,
    hasTShirt: event.hasTShirt,
    signupDeadline: event.signupDeadline,
    questions: event.questions,
    amazonContacts: event.amazonContacts,
    externalContacts: event.externalContacts,
    communityOrganizations: event.communityOrganizations,
    heroImageUrl: event.heroImageUri,
    visibility: (event.visibility as Visibility) ?? Visibility.PUBLIC,
  };
}

/**
 * Maps a Shift (from API) to ShiftInput (for form state).
 * Dates are cast rather than defaulted — if the backend returns undefined,
 * the date picker will render empty and validateShifts() will block submission.
 */
export function mapShiftToShiftInput(shift: Shift): ShiftInput {
  return {
    shiftId: shift.shiftId,
    name: shift.name,
    startDateTime: shift.startDateTime as Date,
    endDateTime: shift.endDateTime as Date,
    maxSignup: shift.maxSignup,
    useEventLocation: shift.useEventLocation,
    location: shift.location,
    shiftTimingType: shift.shiftTimingType,
    shiftLength: shift.shiftLength,
  };
}
